import { z } from 'zod';

import { assertNever } from '../assertNever';
import { DelinquentInterestSettingId, FeeId, PaymentConfigId, PaymentDueId, WorkoutPlanId } from '../BrandedIds';
import { zodBrandedUuid, zodDateOrString } from '../utils';
import {
  NonNegativeFloat,
  NonNegativeInteger,
  NonNegativeMonetaryValue,
  PositiveInteger,
  PositiveMonetaryValue,
} from '../validations/fields';

export const WorkoutPlanType = z.enum(['paymentDeferment', 'repaymentPlan', 'forbearance', 'flexModification']);
export type WorkoutPlanType = z.infer<typeof WorkoutPlanType>;

export const WorkoutPlanBase = z.object({
  id: zodBrandedUuid<WorkoutPlanId>(),
  status: z.enum(['offered', 'active', 'cancelled', 'completed', 'archived']), // archived is for mistakenly added & should not be reported
  startDate: zodDateOrString,
  endDate: zodDateOrString, // scheduled end date (aka last day the plan is active. the plan should be marked as completed at the very end of this day)

  createdAt: zodDateOrString,
  updatedAt: zodDateOrString,

  /**
   * @TODO Offered date:
   * - @TODO Flex modification: The date the modification was offered to the borrower
   * - @TODO Forbearance: The date the forbearance was offered to the borrower
   * - @TODO Repayment plan: The date the repayment plan was offered to the borrower
   * - @TODO Deferral: The date the deferral was offered to the borrower
   */
  offeredDate: zodDateOrString.optional(),

  /**
   * @TODO Accepted date:
   * - @TODO Flex modification: The date the borrower made their first payment toward the plan
   * - @TODO Forbearance:
   *     For suspended-payments: The date the borrower accepted the plan.
   *     For reduced-payments: The date the borrower made their first payment toward the plan
   * - @TODO Repayment plan: The date the borrower made their first payment toward the plan
   * - Deferral: N/A (Deferrals go from offered -> completed)
   */
  acceptedDate: zodDateOrString.optional(),

  /**
   * Completed date:
   * - Flex modification: The date the modification became permanent
   * - @TODO Forbearance: The date the forbearance period ended (without early cancellation)
   * - @TODO Repayment plan: The date the borrower made their final payment and reinstated the loan
   * - @TODO Deferral: The date the deferral went from offered -> completed, and the principal amount was forbeared
   */
  completedDate: zodDateOrString.optional(),
  archivedDate: zodDateOrString.optional(),
  cancelledDate: zodDateOrString.optional(),

  delinquentInterestSettingId: zodBrandedUuid<DelinquentInterestSettingId>().optional(),
});
export type WorkoutPlanBase = z.infer<typeof WorkoutPlanBase>;

// These were originally part of the base, but actually aren't needed for all workout plans
const WorkoutPlanWithAmounts = WorkoutPlanBase.extend({
  principalAmount: NonNegativeMonetaryValue,
  interestAmount: NonNegativeMonetaryValue,
  feeAmount: NonNegativeMonetaryValue,
  escrowAmount: NonNegativeMonetaryValue,
  totalAmount: NonNegativeMonetaryValue,

  hasPrincipalAndInterest: z.boolean(),
  hasFees: z.boolean(),
  hasEscrow: z.boolean(),

  paymentDueIds: z.array(zodBrandedUuid<PaymentDueId>()),
  chargeIds: z.array(zodBrandedUuid<FeeId>()),

  paymentConfigId: zodBrandedUuid<PaymentConfigId>().optional(), // TODO: Deprecate this
});

export const WorkoutPlanPaymentDueStatus = z.object({
  amountRemaining: NonNegativeMonetaryValue,
  fulfilledAt: zodDateOrString.optional(),
  isLate: z.boolean(),
});
export type WorkoutPlanPaymentDueStatus = z.infer<typeof WorkoutPlanPaymentDueStatus>;

export const WorkoutPlanPaymentDue = z.object({
  total: NonNegativeMonetaryValue,
  paymentDate: zodDateOrString,
  dStatus: WorkoutPlanPaymentDueStatus,
});
export type WorkoutPlanPaymentDue = z.infer<typeof WorkoutPlanPaymentDue>;

export const WorkoutPlanIneligibleModification = z.object({
  type: WorkoutPlanType,
  reason: z.string(),
});
export type WorkoutPlanIneligibleModification = z.infer<typeof WorkoutPlanIneligibleModification>;

export const WorkoutPlanWorkflowInformation = z.object({
  completedBorrowerResponsePackageReceived: z.boolean(),
  /**
   * An "ineligible workout option" is a workout option the lender decided not to offer the borrower
   * These ineligibleModifications are used in the pdf documents only and don't affect willow business logic
   */
  ineligibleModifications: z.array(WorkoutPlanIneligibleModification),
});
export type WorkoutPlanWorkflowInformation = z.infer<typeof WorkoutPlanWorkflowInformation>;

/** DEFERMENT WORKOUT PLAN */
export const DefermentWorkoutPlan = WorkoutPlanWithAmounts.extend({
  type: z.literal('paymentDeferment'),
  reason: z.enum(['naturalDisaster']).optional(),
});
export type DefermentWorkoutPlan = z.infer<typeof DefermentWorkoutPlan>;

/** REPAYMENT WORKOUT PLAN */
export const RepaymentWorkoutPlan = WorkoutPlanWithAmounts.extend({
  type: z.literal('repaymentPlan'),
  paymentsProcessed: z.int().nonnegative().optional(),
  numberOfPayments: z.int().positive(),
});
export type RepaymentWorkoutPlan = z.infer<typeof RepaymentWorkoutPlan>;

/**
 * FORBEARANCE WORKOUT PLAN
 * Forbearance is offered when a borrower is experiencing a financial hardship.
 * During this period the borrower’s payments are reduced or suspended, but we'll keep generating payments due and applying as normal.
 * * late fees don't accrue while the plan is active
 * * the loan will still be reportable as delinquent
 *
 * After the forbearance period (successful or failed), the lender must determine whether the borrower is eligible for a payment deferral or flex mod
 */
export const ForbearanceCancellationReason = z.enum([
  'insufficientPayments',
  'noLongerEligible',
  'evaluatingDifferentPlan',
  'loanReinstated',
  'repaymentPlanSelected',
  'defermentSelected',
  'flexModificationSelected',
]);
export type ForbearanceCancellationReason = z.infer<typeof ForbearanceCancellationReason>;

export const ForbearanceBasePlan = WorkoutPlanBase.extend({
  type: z.literal('forbearance'),
  subType: z.enum(['formal', 'disaster', 'special', 'notApplicable']),
  programMoratoriumStartDate: zodDateOrString.optional(), // this is for reporting only (ie a specific covid forbearance program started on ## date)
  startDate: zodDateOrString,
  originalTermMonths: z.int().positive().max(6), // ititial terms can go up to 6-months, with option for lender to extend to 12-months
  // A plan can be extended multiple times, with the standard maximum being up to 12-months
  // However, GSEs have special programs that can extend for longer periods with explicit approval (ie fannie mae made an exception during covid and allowed 18-month extensions)
  extensions: z.array(
    z.object({
      extensionTermMonths: z.int().positive(), // standard is maximum 12-months, but there are exceptions so leaving this open
      extensionDate: zodDateOrString,
    }),
  ),
  drawsAllowed: z.boolean(),
  // As payments come in, these planPaymentsDue are "fulfilled", but they do not change normal payment application.
  planPaymentsDue: z.array(WorkoutPlanPaymentDue), // will be $0 for suspended forbearance, but this will make tracking associated payments due easier
  workflowInformation: WorkoutPlanWorkflowInformation,
  imminentDefault: z.boolean(), // Indicates the loan has been placed in forbearance because the borrower has been determined to be in imminent default

  terminationDate: zodDateOrString.optional(), // early termination scheduled date (plan will be active until the very end of this day)
  cancellationReason: ForbearanceCancellationReason.optional(),
});
const SuspendedForbearancePlan = ForbearanceBasePlan.extend({
  forbearanceType: z.literal('suspended'),
});
export type SuspendedForbearancePlan = z.infer<typeof SuspendedForbearancePlan>;

const ReducedForbearancePlan = ForbearanceBasePlan.extend({
  forbearanceType: z.literal('reduced'),
  partialPaymentAmount: z.number().nonnegative(), // will match planPaymentsDue.total, but is easier to manage displays to store here
});
export type ReducedForbearancePlan = z.infer<typeof ReducedForbearancePlan>;

export const ForbearanceWorkoutPlan = z.discriminatedUnion('forbearanceType', [
  SuspendedForbearancePlan,
  ReducedForbearancePlan,
]);

export type ForbearanceWorkoutPlan = z.infer<typeof ForbearanceWorkoutPlan>;

/**
 * FLEX MODIFICATION WORKOUT PLAN
 */
export const FlexModificationModification = z.object({
  interestRate: NonNegativeFloat,
  loanTerm: NonNegativeInteger,
  unpaidPrincipalBalance: NonNegativeMonetaryValue,
  statementEscrowAmount: NonNegativeMonetaryValue,
  deferredPrincipalAmount: NonNegativeMonetaryValue.optional(),
});
export type FlexModificationModification = z.infer<typeof FlexModificationModification>;

export const FlexModificationPermanentModification = z.object({
  interestRate: NonNegativeFloat,
  loanTerm: PositiveInteger,
  unpaidPrincipalBalance: PositiveMonetaryValue,
  effectiveDate: zodDateOrString,
  deferredPrincipalAmount: NonNegativeMonetaryValue.optional(),
});
export type FlexModificationPermanentModification = z.infer<typeof FlexModificationPermanentModification>;

/**
 * A flex modification is offered to a borrower in order to offer more affordable payment terms.
 * Any outstanding principal, interest, advances and some fees are capitalized into the loan’s unpaid principal
 * balance (UPB) and a new interest rate and/or loan term length is offered to the borrower.
 *
 * The workout plan is used to track the Trial Period Plan in which borrowers must make payments at the modified terms for a specified number of months before the flex modification becomes permanent
 *
 * The loan keeps billing as normal during the trial period. So while we expect the reduced trial payments, the loan is expected to become
 * delinquent during this time (if its not already), and is still reportable as delinquent.
 *
 * Documentation for more details: https://www.figma.com/board/561q5ifrz94gK0THyHr6Du/FNMA-Delinquency-Workflows?node-id=258-69&t=moP6h45puOmHJQ8y-0
 */
export const FlexModificationWorkoutPlan = WorkoutPlanBase.extend({
  type: z.literal('flexModification'),

  reason: z.enum(['naturalDisaster']).optional(),

  // WorkoutPlanBase.startDate = trial start date
  trialTermMonths: z.number().int().min(1).max(12), // fannie mae loans are either 3 or 4 months, but possibly other GSE or mortgage providers offer diff terms

  evaluationNoticeSentDate: zodDateOrString, // first payment must be made within 14 days of this date

  // This is what the loan will be modified to after the trial period. While this workout plan is active, the loan continues to operate with the normal terms, payments due, and delinquency status
  modification: FlexModificationModification,

  // As payments come in, these planPaymentsDue are "fulfilled", but they do not change normal payment application.
  planPaymentsDue: z.array(WorkoutPlanPaymentDue),

  // To start we're going to collect this information up front when creating the workout plan,
  // but, this will be moved to a workflow at some point in the future
  // Figma flow: https://www.figma.com/board/561q5ifrz94gK0THyHr6Du/FNMA-Delinquency-Workflows?node-id=255-564&t=HT8wF2FN9mWfknH1-0
  workflowInformation: WorkoutPlanWorkflowInformation,

  paymentDueIds: z.array(zodBrandedUuid<PaymentDueId>()).optional(), // Make required in the future
  chargeIds: z.array(zodBrandedUuid<FeeId>()).optional(), // Make required in the future
});
export type FlexModificationWorkoutPlan = z.infer<typeof FlexModificationWorkoutPlan>;

export const FlexModificationArrearages = z.object({
  unpaidPrincipal: z.number().positive(),
  unpaidInterest: z.number().nonnegative(),
  unpaidFeesIncludingDeferred: z.number().nonnegative(),
  unpaidAdvancesIncludingDeferred: z.number().nonnegative(),
  currentOutstandingBalance: z.number().positive(),
  capitalizedChargeIds: z.array(zodBrandedUuid<FeeId>()),
  capitalizedPaymentDueIds: z.array(zodBrandedUuid<PaymentDueId>()),
});
export type FlexModificationArrearages = z.infer<typeof FlexModificationArrearages>;

/** ------------------ */
export const WorkoutPlan = z.discriminatedUnion('type', [
  DefermentWorkoutPlan,
  RepaymentWorkoutPlan,
  ForbearanceWorkoutPlan,
  FlexModificationWorkoutPlan,
]);
export type WorkoutPlan = z.infer<typeof WorkoutPlan>;

export const isDefermentWorkoutPlan = (workoutPlan: WorkoutPlan): workoutPlan is DefermentWorkoutPlan =>
  workoutPlan.type === 'paymentDeferment';

export const isRepaymentWorkoutPlan = (workoutPlan: WorkoutPlan): workoutPlan is RepaymentWorkoutPlan =>
  workoutPlan.type === 'repaymentPlan';

export const isForbearanceWorkoutPlan = (workoutPlan: WorkoutPlan): workoutPlan is ForbearanceWorkoutPlan =>
  workoutPlan.type === 'forbearance';

export const isFlexModificationWorkoutPlan = (workoutPlan: WorkoutPlan): workoutPlan is FlexModificationWorkoutPlan =>
  workoutPlan.type === 'flexModification';

export const isPaymentPlanWorkoutPlan = (
  workoutPlan: WorkoutPlan,
): workoutPlan is ForbearanceWorkoutPlan | FlexModificationWorkoutPlan => {
  switch (workoutPlan.type) {
    case 'paymentDeferment':
    case 'repaymentPlan':
      return false;
    case 'forbearance':
    case 'flexModification':
      return true;
    default:
      assertNever(workoutPlan);
  }
};

export const DefermentWorkoutPlanInput = DefermentWorkoutPlan.omit({
  id: true,
  status: true,
  createdAt: true,
  updatedAt: true,
  paymentDueIds: true,
  chargeIds: true,
  principalAmount: true,
  interestAmount: true,
  feeAmount: true,
  escrowAmount: true,
  totalAmount: true,
});
export type DefermentWorkoutPlanInput = z.infer<typeof DefermentWorkoutPlanInput>;

export const RepaymentWorkoutPlanInput = RepaymentWorkoutPlan.omit({
  id: true,
  status: true,
  createdAt: true,
  updatedAt: true,
  paymentDueIds: true,
  chargeIds: true,
  principalAmount: true,
  interestAmount: true,
  feeAmount: true,
  escrowAmount: true,
  totalAmount: true,
});
export type RepaymentWorkoutPlanInput = z.infer<typeof RepaymentWorkoutPlanInput>;

export const WorkoutPlanInput = z.discriminatedUnion('type', [DefermentWorkoutPlanInput, RepaymentWorkoutPlanInput]);
export type WorkoutPlanInput = z.infer<typeof WorkoutPlanInput>;

export type RepaymentPlanCounts = {
  numExistingInstallments: number;
  numInstallments: number;
};
